import Modal from '@src/components/modal/Modal';
import styles from './SelectModal.module.scss';
import CloseIcon from '@src/assets/icons/close.svg?react';
import Select from '@src/components/select/Select';
import { useQueryDictionary } from '@src/hooks';
import { SelectOption, UserRoleEnum } from '@src/types';
import Button from '@src/components/button/Button';
import { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import { useGetAccountSchoolsLazyQuery } from '@src/api/graphql-codegen';
import { countryOptions } from '@src/constants';
import { hasAllValues } from '@src/utils/validation';

type Props = {
    open: boolean;
    onClose: () => void;
    onSave: (userType: UserRoleEnum, account?: SelectOption, schools?: SelectOption[], country?: SelectOption) => void;
}

function SelectModal (props: Props) {
    const { open, onClose, onSave } = props;

    const [countryFilter, setCountryFilter] = useState<SelectOption>();
    const [account, setAccount] = useState<SelectOption>();
    const [school, setSchool] = useState<SelectOption>();
    const [userType, setUserType] = useState<UserRoleEnum>();

    const accountOptions = useQueryDictionary({dictionary: 'Customers'});
    const [schoolOptions, setSchoolOptions] = useState<SelectOption[]>([]);

    const [getSchools, {loading: schoolOptionsLoading}] = useGetAccountSchoolsLazyQuery({fetchPolicy: 'network-only'})

    useEffect(() => {
        if (account) {
          const fetchSchools = async () => {
            const res = await getSchools({ variables: { input: { id: account.value } } })
            return res.data?.accountSchools
          }
          fetchSchools().then(res => {
            if (res) {
                setSchoolOptions((res.schools ?? []).map(s => {
                return {
                  value: s?.id ?? '',
                  label: s?.name ?? '',
                };
              }));
            }
          }).catch(console.error);
        }
    }, [getSchools, account]);

    const handleResetFilters = useCallback(() => {
        setAccount(undefined);
        setSchool(undefined);
        setUserType(undefined);
    }, [])

    const handleChangeFilter = useCallback((setter: Dispatch<SelectOption>, fieldName?: string) => (o: SelectOption) => {
        setter(o);
        if (fieldName === 'Account' && o.value) {
          getSchools({ variables: { input: { id: o.value } } });
        } else if (fieldName === 'Country') {
            handleResetFilters();
        }
    }, [getSchools, handleResetFilters]);

    const handleClose = () => {
        onClose()
    }

    const searchObj = useMemo(() => ({
        country: countryFilter?.value || undefined,
        accountId: account?.value || undefined,
    }), [countryFilter, account]);
  
    const hasSearchVariables = useMemo(() => hasAllValues(searchObj), [searchObj]);

    const handleSave = () => {
        if (!hasSearchVariables) {
            return
        }
        if (school) {
            onSave(userType || UserRoleEnum.DistrictAdmin, account, [school], countryFilter)
        } else {
            if (schoolOptions.length > 1) {
                schoolOptions.splice(0, 0, {label: "All Schools", value: "All Schools"})
                onSave(userType || UserRoleEnum.DistrictAdmin, account, schoolOptions, countryFilter)
            } else {
                onSave(userType || UserRoleEnum.DistrictAdmin, account, schoolOptions, countryFilter)
            }
        }
        
    }
    return (
        <Modal open={open} onClose={handleClose} closeButton={false} className={styles.wrapper} >
            <div>
                <button onClick={onClose} className={styles.closeWrapper}>
                    <CloseIcon className={styles.closeIcon} />
                </button>
                <div className={styles.title}>{'Super Admin Setting'}</div>
                <div className={styles.content}>
                    <Select 
                        label={'Country'} menuClassName={styles.selectorStyle}
                        inDropDown={true} showSearch={false} className={styles.accountSelect}
                        value={countryFilter?.value} onChange={handleChangeFilter(setCountryFilter, 'Country')}
                        options={countryOptions} />
                    <Select 
                        label='Account' inDropDown={true} 
                        menuClassName={styles.selectorStyle}
                        value={account?.value} options={accountOptions} onChange={handleChangeFilter(setAccount, 'Account')} />
                    <Select
                        menuClassName={styles.selectorStyle}
                        label='School' disabled={schoolOptionsLoading} loading={schoolOptionsLoading}
                        value={school?.value} options={schoolOptions} onChange={handleChangeFilter(setSchool, 'School')} />
                </div>
                <Button label='Save' disabled={!account || schoolOptionsLoading} className={styles.saveButton} onClick={handleSave} />
            </div>
        </Modal>
    )
}

export default SelectModal